import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import './App.css';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SKUMasterPage = lazy(() => import('./pages/SKUMasterPage'));
const ProcurementPage = lazy(() => import('./pages/ProcurementPage'));
const PackingPage = lazy(() => import('./pages/PackingPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const TransfersPage = lazy(() => import('./pages/TransfersPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const AdsPage = lazy(() => import('./pages/AdsPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const PlatformPayoutsPage = lazy(() => import('./pages/PlatformPayoutsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ContributionAnalysisPage = lazy(() => import('./pages/ContributionAnalysisPage'));
const BlinkitPage = lazy(() => import('./pages/platform/BlinkitPage'));
const InstamartPage = lazy(() => import('./pages/platform/InstamartPage'));
const ZeptoPage = lazy(() => import('./pages/platform/ZeptoPage'));
const SKUMappingPage = lazy(() => import('./pages/platform/SKUMappingPage'));
const StockAdjustmentsPage = lazy(() => import('./pages/StockAdjustmentsPage'));

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-700"></div>
  </div>
);

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" replace /> : <LoginPage />}
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/skus"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><SKUMasterPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/procurement"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><ProcurementPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/packing"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><PackingPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><InventoryPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/transfers"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><TransfersPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><SalesPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/ads"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><AdsPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><PaymentsPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/platform-payouts"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><PlatformPayoutsPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><ContributionAnalysisPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/blinkit"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><BlinkitPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/instamart"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><InstamartPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/zepto"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><ZeptoPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sku-mapping"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><SKUMappingPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/stock-adjustments"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><StockAdjustmentsPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
